// penPressure — 筆圧 mapping 設定の pure logic + localStorage アダプタ
// (consult 2026-05-09 / architecture.md §19.15).
//
// ## 動作仕様
//
// **mapping (統一式)**: CSP 流相対スケール + ペン種別 gamma カーブ
// (architecture.md §19.15):
//   - `effective = min + (1 - min) * pow(p, gamma)`
//   - gamma table は drawing-core 側 (`PRESSURE_GAMMA`) に定義 + export, web は
//     import して使う (= SSoT、二重管理回避)
//   - `widthEnabled` / `alphaEnabled` が false なら mapping を skip (= 1.0 倍)
//   - 2026-05-09 (consult ペン統合): 全ペン全軸 gamma=1 (線形). 旧 `normal.alpha=3`
//     の累乗 gamma 例外は撤廃 — `'brush'` の per-stamp alpha 自体が累乗カーブ
//     (Step 3 で実装) で連続化されるため、筆圧 mapping 側で別途累乗を入れる
//     必要がない (= 二重累乗回避).
//   - β でカーブエディタ (control points) に拡張可能で、`(min, gamma)` は
//     スプライン制御点に無変換で展開できる設計 (data 移行不要)
//
// **配信側で適用**: `DrawingCanvas.tsx` の pointer event 経路で「設定 → effective
// `p` 計算 → `stroke.segment.p` に乗せる」. 受信側 rasterizer は user 設定を一切
// 持たず、額面通り `pt.p` を効果対象に反映する (= 二重 mapping 防止).
//
// **保存先**: `localStorage['oekaki:pen-pressure:<penKind>']` に JSON で
// `{ widthEnabled, widthMin, alphaEnabled, alphaMin }`. ペン種ごとに **別キーで**
// 保持する (= 旧版踏襲の単純さ + テスト容易性). サーバ同期は β+.
//
// ## ペン種別の効果対象 (architecture.md §19.15 表、consult 2026-05-09 ペン統合):
//
//   | ペン     | 太さ筆圧  | alpha 筆圧 |
//   |---------|----------|-----------|
//   | brush   | ✅ ON 可  | ✅ ON 可 (旧 normal + water 統合, alpha 軸はペン統合で常に有効)
//   | flat    | ✅ ON 可  | **意味的に不可** (= stroke 全体 uniform alpha と矛盾、UI は disabled で表示)
//   | marker  | ✅ ON 可  | ✅ ON 可 (= max 蓄積 + 濃さ筆圧連動、コピックマーカー風、consult 2026-05-09 試験追加)
//   | eraser  | ✅ ON 可  | —
//
// UI 側 (`PenPressureConfig.tsx`) はペン種に応じて表示する toggle を切り替える.
// 効果対象が `—` のものは toggle を出さない (= 設定しても無効なのでユーザを
// 混乱させない).
//
// `'flat'` の濃さ筆圧は **表示するが disabled** (= グレーアウト + 操作不可) で
// レンダリングして「このペンでは使えない」を視覚化する (consult 2026-05-09 ユーザ
// 実機 fb). 内部 state は `DEFAULT_PEN_PRESSURE.flat.alphaEnabled = false` で固定.
//
// Stage Y (consult 2026-05-31, `marker-pen-deprecation-plan.md`): drawing-core 側
// `'flat'` が `pt.pAlpha` を参照するように統合済 (= `'marker'` と同じ alpha 軸動的
// 変動経路に乗る). ただし `alphaEnabled=false` (default) のとき `computeEffectivePressure`
// が `pAlpha=1.0` を明示返却するため、drawing-core 側で alphaScale=1.0 になり既存
// ユーザの uniform 挙動は **完全互換**. `'marker'` の protocol 撤去は Stage X
// (β 期 = Phase 10) で実施予定.
//
// `'marker'` (consult 2026-05-09 試験追加): `'flat'` の本質 (= stroke 内 uniform =
// max 蓄積モード) を維持しつつ濃さ筆圧連動を **default ON** にした第 3 のペン.
// 直近実機検証で「`'flat'` + 濃さ筆圧連動」がコピックマーカー風として綺麗に
// 出たため別ペン種化. 太さ・濃さ両軸 default ON、gamma=1 (線形) で他ペンと統一.
//
// ## bellOnRoomin との同流儀
//
// `createBellOnRoominStore` と同じパターン (probe + silent fallback):
// - `probeBrowserStorage()`: localStorage が利用可能か sentinel write/read で確認
// - `read(penKind)`: 不在 / 不正値 / storage 不可 → デフォルト値
// - `write(penKind, state)`: storage 不可なら no-op
//
// (architecture.md §19.15 / consult 2026-05-09 / 旧 `JTabletConfigCanvas`
//  ON/OFF + min スライダ をシンプル化した α 版)

import { PRESSURE_GAMMA } from '@oekaki/drawing-core';
import type { StrokePen } from '@oekaki/protocol';

/** localStorage キーの prefix. ペン種ごとに `:<penKind>` を付けて使う. */
const STORAGE_KEY_PREFIX = 'oekaki:pen-pressure';

/**
 * デフォルト min 値 (太さ・alpha 共通).
 *
 * 2026-05-15 ユーザ実機 fb で 0.3 → **0** に変更 (= 筆圧 0 で width/alpha も 0、
 * 入り抜き最大化、軽く触れたら消える挙動). UI スライダは 0..1 を許容するので
 * 軽い線を残したいユーザは個別に上げる. localStorage 設定済ユーザは自身の値が
 * 優先され影響を受けない、新規 / 未保存ユーザのみ 0 で起動.
 */
export const DEFAULT_PRESSURE_MIN = 0;

/**
 * ペン種ごとの筆圧設定 state.
 *
 * - `widthEnabled` / `alphaEnabled`: 各効果の ON/OFF トグル. OFF のときは
 *   mapping を skip (= 1.0 倍, ペン UI 設定そのまま).
 * - `widthMin` / `alphaMin`: 0..1 の min 値 (= 筆圧 0 のときの効果倍率).
 *   `widthMin = 0.3` なら筆圧 0 で 30%, 筆圧 1 で 100%.
 *
 * 効果対象が無いペン (`alpha` の widthEnabled、`normal/eraser` の alphaEnabled)
 * でも state は持つが、`computeEffectivePressure` 側で「設定が ON でも効果対象が
 * 無ければ skip する」のではなく、UI 側で該当 toggle を **出さない** ことで
 * 値が `false` で固定される運用にする (= state は単純化).
 */
export interface PenPressureSettings {
  readonly widthEnabled: boolean;
  /** 0..1, 筆圧 0 のときの太さ倍率. */
  readonly widthMin: number;
  readonly alphaEnabled: boolean;
  /** 0..1, 筆圧 0 のときの alpha 倍率. */
  readonly alphaMin: number;
}

/**
 * ペン種別のデフォルト設定 (consult 2026-05-09 ペン統合).
 *
 * - 太さ筆圧 / 濃さ筆圧: 全ペンで **デフォルト OFF** (2026-05-15 ユーザ実機 fb で
 *   変更. 元は 太さ default ON / `'marker'` のみ 濃さ default ON だったが、初回
 *   ユーザが「マウスで描いてもペンタブで描いても挙動が一定」を期待する声が多く、
 *   ペンタブユーザが UI トグルを ON にする運用に倒した. `'flat'` の濃さは引き続き
 *   **意味的に不可なので OFF + UI は disabled で表示** (= stroke 全体 uniform
 *   alpha と濃さ stroke 内変動の矛盾を視覚化、consult 2026-05-09).
 *
 * `widthMin` / `alphaMin` は全ペン共通 `DEFAULT_PRESSURE_MIN` (= 0、2026-05-15
 * ユーザ実機 fb で 0.3 → 0 に変更、marker も 0.15/0.5 → 0 に揃え).
 */
export const DEFAULT_PEN_PRESSURE: Readonly<Record<StrokePen, PenPressureSettings>> = {
  brush: {
    // ペン統合 (consult 2026-05-09): 旧 `normal` + `water` を統合. alpha 軸は
    // protocol 上は常に有効化可能だが、**default は OFF** (consult 2026-05-09 /
    // Step 5c). CSP / Photoshop の通常ブラシ (= Gペン / Hard Round) の default は
    // 「不透明度筆圧 OFF」が標準で、palette A 値 100% で真っ黒線画が描けるのが
    // 期待挙動. 入り抜きで濃さも変動させたいユーザは UI で alpha 筆圧トグルを
    // ON にする (= UI 側 `SHOW_ALPHA_TOGGLE.brush = true` のまま、トグル表示は
    // 維持して初期値だけ OFF).
    //
    // 2026-05-15 ユーザ実機 fb: 太さ筆圧も default OFF に倒す. マウス / ペンタブ
    // どちらでも描き始めの挙動を一定にしたい意図. ペンタブユーザは UI トグルで ON.
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  flat: {
    // 旧 `alpha` (一般透過) から rename. 2026-05-15 ユーザ実機 fb で太さ筆圧
    // default を ON → OFF に変更 (= 全ペン同方針). ペンタブユーザは UI トグルで ON.
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    // 濃さ筆圧 default OFF + UI で disabled 表示 (consult 2026-05-09 ユーザ実機 fb):
    // `'flat'` の本質 (= stroke 全体 uniform alpha + max 蓄積モード) と濃さ筆圧
    // 連動は意味的に矛盾する (= stroke 内で alpha が動くと「均一塗り」が破綻).
    // PenPressureConfig 側で `ALPHA_DISABLED.flat = true` で行を表示するが操作
    // 不可に倒し、内部 state も OFF で固定するのが整合的.
    //
    // Stage Y (consult 2026-05-31, `marker-pen-deprecation-plan.md`): drawing-core
    // 側は `'flat'` も `pt.pAlpha` を参照する経路に統合済 (= `'marker'` と内部
    // 完全同一). `alphaEnabled=false` (default) のとき `computeEffectivePressure`
    // が `pAlpha=1.0` を明示返却 → drawing-core 側 alphaScale=1.0 で従来 uniform
    // 挙動が完全互換. 「`'flat'` で alpha 筆圧 ON にできる」設計上の意味解釈は
    // 別軸 (= UI 視覚化) で運用. `'marker'` の protocol 撤去は Stage X.
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  marker: {
    // 2026-05-09 (consult 試験追加): `'flat'` の max 蓄積モード (= stroke 終端
    // 1 回 commit + 蓄積 max) を維持しつつ、濃さ筆圧連動を別ペン種として独立化.
    // drawing-core 側で per-stamp alpha は線形 (`stroke.alpha * pAlphaPressure`)
    // で `'brush'` の累乗カーブとは区別.
    //
    // 2026-05-15 ユーザ実機 fb: 全ペン同方針で 太さ / 濃さ筆圧とも default OFF、
    // min は `DEFAULT_PRESSURE_MIN` (= 0) に揃える. 元の (d) ハイブリッド案
    // (widthMin 0.15 / alphaMin 0.5) は「コピック筆ペン風」を狙ったが、ユーザは
    // 「初回はマウスでも一様に描ける状態」を望むため、min を底辺 0 にして UI で
    // 個別調整に倒す方針. ペンタブユーザは UI で各 min を持ち上げて (d) 風を再現可.
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  eraser: {
    // 消ゴム筆圧 default は 2026-05-15 ユーザ実機 fb で ON → OFF に変更
    // (= 全ペン同方針). 必要なら UI toggle で ON にする.
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  texture: {
    // texture spike (texture-brush-design.md §8): brush 相当の筆圧設定で埋める.
    // 全軸 default OFF (= 他ペン同方針). preset / 筆圧連動の詳細は横展開フェーズ.
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  tone: {
    // スクリーントーン (texture-brush-design.md §12.2): 筆圧グラデは不採用
    // (consult 2026-06-20). 全軸 default OFF. density は番手スライダ (penParams)
    // で stroke 単位に固定する.
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  airbrush: {
    // エアブラシ (airbrush-design-2026-08-15.md §2.5): drawing-core 側
    // (`strokeRenderer.ts` の `case 'airbrush'`) は `'brush'`/`'texture'` と同じ
    // 2 軸筆圧 (width=pt.p、alpha=pt.pAlpha) を明示的に採用している — 太筆圧で
    // ink 判定域 (スタンプ径) が広がり、濃筆圧で満額 ink の per-stamp alpha が
    // 上がる (濃度そのものは per-stamp alpha ではなく閾値ハッシュが担うが、
    // 両軸とも kernel が実際に消費する). よって「濃さ筆圧は要検討」だった設計書の
    // TODO は「texture と同じ扱いで両軸とも表示・操作可」に解決する — 太さ筆圧が
    // 自然に効くのは事実だが、濃さ筆圧を隠す理由 (= `'flat'` の uniform alpha の
    // ような意味的矛盾) が airbrush には無い。他ペン同様 default は両軸 OFF
    // (2026-05-15 fb の全ペン共通方針)、ペンタブユーザは UI トグルで ON にする。
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  // クレヨン / パステル / ドット (phase11-light-pens-2026-08-21.md §1-§3/§6): 質感/線画
  // タブの chip として露出済 (`modernTools.ts` MODERN_BRUSH_SUBTOOLS 参照、ドットは
  // Wasm parity 解消済み) — 他ペン共通方針 (全軸 default OFF) で埋める.
  // `Record<StrokePen, ...>` 充足のため定義する.
  crayon: {
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  pastel: {
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
  dot: {
    widthEnabled: false,
    widthMin: DEFAULT_PRESSURE_MIN,
    alphaEnabled: false,
    alphaMin: DEFAULT_PRESSURE_MIN,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// pure mapping
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `min..1` の範囲に 0..1 の入力 `p` を線形マップする.
 *
 *   `min + (1 - min) * p`
 *
 * - `enabled === false` なら 1.0 を返す (= mapping skip = ペン UI 設定そのまま).
 * - 入力 `p` が undefined なら 1.0 を返す (= 筆圧情報なしの場合は最大効果).
 * - 入力 `p` が 0..1 範囲外なら clamp してから mapping.
 * - `min` が 0..1 範囲外でも clamp.
 *
 * `min === 0` なら筆圧 0 で効果が 0 になるが、現在 UI で 0 まで下げられる仕様
 * (= ユーザが「軽いタッチで全く描かない」を選びたい場合がある).
 */
export const applyPressureMin = (
  enabled: boolean,
  min: number,
  pRaw: number | undefined,
): number => {
  if (!enabled) return 1.0;
  if (pRaw === undefined) return 1.0;
  const p = Math.max(0, Math.min(1, pRaw));
  const m = Math.max(0, Math.min(1, min));
  return m + (1 - m) * p;
};

/**
 * `computeEffectivePressure` の戻り値.
 *
 * 2 軸独立設計 (consult 2026-05-09 / Step 5c):
 * `'brush'` で「太さ筆圧」と「濃さ筆圧」を **独立に** 設定できるよう、protocol
 * `StrokePoint.{ p, pAlpha }` の 2 field に対応した **2 値返却**. 旧来の単一値
 * 返却では「太さ ON + 濃さ ON で widthMin と alphaMin が異なるとき軸を 1 つ
 * 選ばざるを得ず、もう片方が silent に無効化される」問題があった.
 *
 * **片軸 OFF 時の意味解釈** (consult 2026-05-09 ユーザ実機 fb の fix):
 * 片軸 OFF のときは `1.0` を **明示返却** する. 旧仕様 (= `undefined` で omit)
 * は drawing-core 側 fallback `pt.pAlpha ?? pt.p ?? 1.0` (旧 client 後方互換用)
 * が効いてしまい、「ブラシ濃さ筆圧 OFF + 太さ ON」のときに `pt.p` (= 太さ用の
 * 値) が alpha 軸にも漏れていたバグの fix. 両軸 OFF / pRaw omit 条件のときだけ
 * 両 `undefined` で完全 omit (= protocol を細く保つ + 旧 client 後方互換維持).
 */
export interface EffectivePressure {
  /**
   * width 軸用 effective pressure.
   * - 太さ筆圧 ON のとき: `widthMin` 反映の mapping 値.
   * - 太さ筆圧 OFF + 濃さ筆圧 ON のとき: `1.0` (= width 軸固定の意図を明示、
   *   drawing-core fallback 経由を防ぐ).
   * - 両軸 OFF / 非筆圧デバイス / 範囲外 / undefined 等の omit 条件: `undefined`.
   * 受信側 rasterizer は `pt.p ?? 1.0` で fallback.
   */
  readonly p: number | undefined;
  /**
   * alpha 軸用 effective pressure.
   * - 濃さ筆圧 ON のとき: `alphaMin` 反映の mapping 値.
   * - 濃さ筆圧 OFF + 太さ筆圧 ON のとき: `1.0` (= alpha 軸固定の意図を明示、
   *   drawing-core fallback `?? pt.p ?? 1.0` 経由で `pt.p` が漏れるのを防ぐ).
   * - 両軸 OFF / omit 条件のとき: `undefined`.
   * 受信側 rasterizer は `pt.pAlpha ?? pt.p ?? 1.0` の fallback 順で参照する
   * (= drawing-core Step 5b、旧 client 後方互換のため fallback 自体は維持).
   */
  readonly pAlpha: number | undefined;
}

/**
 * 「protocol の `stroke.segment.{p, pAlpha}` に乗せる effective pressure」を
 * 2 軸独立に計算する.
 *
 * 設計の核心 (architecture.md §19.15):
 * - **送信側で 1 回だけ mapping を適用**. 受信側 rasterizer は user 設定を一切
 *   持たない (= 二重 mapping 防止).
 * - 戻り値 `{ p, pAlpha }` は受信側で「効果対象 (太さ・alpha) にそのまま反映
 *   する係数」として使われる. つまり mapping 後の値 (`min + (1-min) * pRaw^gamma`)
 *   を直接 `stroke.segment.{p, pAlpha}` に乗せる.
 *
 * **戻り値の意味** (consult 2026-05-09 ユーザ実機 fb の fix で片軸 OFF 挙動が
 * `undefined` → `1.0` 明示返却に変わった):
 * - `{ p: undefined, pAlpha: undefined }`: `pt.p` / `pt.pAlpha` を両方 **omit**.
 *   以下の条件:
 *   - `pRaw` が undefined / NaN / 範囲外 (= 筆圧情報なし or 不正値)
 *   - `pointerType !== 'pen'` で `pRaw` が 0 / 1 (= マウス等の非筆圧デバイス
 *     fallback、両軸とも触らない)
 *   - **両軸 OFF** (= `widthEnabled === false` かつ `alphaEnabled === false`、
 *     protocol を細く保つ + 旧 client 後方互換維持)
 * - `{ p: 数値, pAlpha: 1.0 }`: 太さ ON / 濃さ OFF (例: eraser / brush default).
 *   `pAlpha` は `undefined` ではなく `1.0` を **明示送信** することで、drawing-core
 *   側 fallback `pt.pAlpha ?? pt.p ?? 1.0` 経由で `pt.p` (= 太さ用の値) が alpha
 *   軸に漏れるのを防ぐ (= 「濃さ筆圧 OFF にしても alpha が動的に変わる」バグ
 *   の fix).
 * - `{ p: 1.0, pAlpha: 数値 }`: 太さ OFF / 濃さ ON (例: flat ユーザが太さトグル
 *   OFF にした場合). 同様に `p` は `1.0` 明示で width 軸固定の意図を伝える.
 * - `{ p: 数値, pAlpha: 数値 }`: 両 ON で **2 軸独立**. brush の太さ + 濃さ
 *   両 ON ユースケース (CSP / Photoshop 流の入り抜き + 濃さ変動).
 *
 * **`pointerType` 引数の意義** (2026-05-09 consult / iPad Apple Pencil バグ修正):
 * iPad Apple Pencil は軽接触で `pressure=0` を出すことがあり、旧来の
 * `pRaw > 0 && pRaw < 1` 判定だと omit → 受信側 `widthScale=1.0` fallback で
 * stamp 最大化バグが発生していた. `pointerType === 'pen'` (= スタイラス) の
 * ときだけ 0..1 全部 valid 扱いにすることで、軽接触は mapping の min 値 (=
 * 細い stamp) が出るようになる. mouse / touch は従来挙動を維持して回帰させない.
 *
 * **gamma table SSoT** (consult 2026-05-09 / architecture.md §19.15):
 * gamma table は `@oekaki/drawing-core` の `PRESSURE_GAMMA` を import して
 * 使う (= 二重管理回避). drawing-core 側 rasterizer は `pt.{p, pAlpha}` を
 * 額面通り受け取って効果対象に反映する契約 (= 二重 mapping 防止) で、
 * `PRESSURE_GAMMA` は両側で参照される定数としてのみ存在する.
 *
 * `penKind` 引数を渡さないと gamma=1 fallback で線形扱い (= 既存挙動と完全
 * 一致、回帰なし).
 */
export const computeEffectivePressure = (
  settings: PenPressureSettings,
  pRaw: number | undefined,
  pointerType?: 'mouse' | 'pen' | 'touch',
  penKind?: StrokePen,
): EffectivePressure => {
  if (pRaw === undefined) return { p: undefined, pAlpha: undefined };

  // 2026-05-09 (consult / iPad Apple Pencil 軽接触 stamp 最大化バグ修正):
  //   pointerType === 'pen' (= スタイラス全般、Apple Pencil 等) は 0..1 全部
  //   valid な実値として扱う:
  //     - pRaw=0 (軽接触) → mapping で min 値が出るので「細い stamp」になる
  //     - pRaw=1 (強押し or HW スパイク) → mapping で max (= 1.0) になる
  //   従来の `pRaw > 0 && pRaw < 1` 判定だと pRaw=0 が omit されて受信側
  //   rasterizer の `pt.p === undefined → widthScale=1.0` fallback に倒れ、
  //   軽接触のはずが最大太さで描かれていた (iPad 実機で再現確認、間欠的).
  //
  // 2026-05-29 (α 公開直前 hotfix / マウス + 筆圧 ON で cursor 円と実描画 width
  // 不一致バグ修正): pen 以外 (mouse / touch / 不明) は **常に両軸 undefined で
  // omit** する. = 筆圧情報がそもそも無いデバイスとして扱い、ペン UI 設定そのまま
  // (= cursor 円と一致) で描く. 旧仕様 (= `pRaw > 0 && pRaw < 1` で valid 扱い) は
  // 「マウスは常に pressure=1.0 を返す」前提だったが、Chrome は押下中の左ボタンに
  // `pressure=0.5` を仕様で返し (= W3C Pointer Events L3、Firefox は 0.5、Safari は
  // 0)、widthEnabled=ON のとき `applyPressureCurve(0, 1, 0.5) = 0.5` で実描画が
  // 設定 width の半分になっていた. cursor 円 (= BrushCursorOverlay) は pen.width を
  // 直接表示しており、両者が一致しないユーザ実機 fb (2026-05-28). 筆圧 ON は
  // 「ペンタブ / スタイラス向け」と再定義し、マウス/touch ではトグル状態に
  // 関係なく無効化する (= 既存 widthEnabled=OFF と同じ挙動に倒す). 消しゴム 60px は
  // 元から widthEnabled=OFF default で一致しており、本 hotfix で「ON にしても
  // 一致する」へ拡大適用となる.
  const isStylus = pointerType === 'pen';
  if (isStylus) {
    // 範囲外 / NaN は omit. 0..1 (両端含む) は valid.
    if (!(pRaw >= 0 && pRaw <= 1)) return { p: undefined, pAlpha: undefined };
  } else {
    // mouse / touch / unknown: 筆圧情報なしデバイスとして両軸 undefined で omit.
    // 受信側 rasterizer の `pt.p ?? 1.0` / `pt.pAlpha ?? pt.p ?? 1.0` fallback で
    // 設定 width / alpha がそのまま反映され、cursor 円と実描画 width が常に一致.
    return { p: undefined, pAlpha: undefined };
  }

  // 両軸 OFF のときは両 undefined で完全 omit (= protocol を細く保つ + 旧 client
  // 後方互換維持). 受信側 rasterizer は `pt.p ?? 1.0` / `pt.pAlpha ?? pt.p ?? 1.0`
  // fallback で初期挙動と一致する.
  if (!settings.widthEnabled && !settings.alphaEnabled) {
    return { p: undefined, pAlpha: undefined };
  }

  // 統一式: out = min + (1 - min) * pow(p, gamma)
  // gamma table (drawing-core SSoT) を引いて軸ごとの gamma を取る.
  // penKind 未指定なら gamma=1 fallback (= 線形、既存挙動と完全一致).
  // 2026-05-09 (consult `'marker'` 試験追加): protocol enum に新ペン種が追加され
  // て drawing-core 側の `PRESSURE_GAMMA` 更新が遅れた場合に `undefined` 参照で
  // 落ちないよう、引いた値が undefined なら線形 (= gamma=1) に倒す. drawing-core
  // 側で当該ペンの gamma が宣言されたら自然にそちらが優先される (= 二重管理回避は
  // 維持). web 側の DEFAULT_PEN_PRESSURE は新ペン種を即追加するため一時的な
  // skew は許容.
  //
  // **型** (= drawing-core 側 const の宣言を `as const satisfies Record<StrokePen, ...>`
  // で書く設計だが、protocol enum 拡張時に drawing-core の更新が遅れると
  // `PRESSURE_GAMMA` の literal key が StrokePen の subset になってしまい
  // indexed access が `noUncheckedIndexedAccess` 下で error になる). web は
  // protocol と歩調を合わせて先に `'marker'` 等を扱えるよう、`Record<StrokePen,
  // {width:number;alpha:number}>` として読む型 widening + `??` 防御の組合せで
  // 一時 skew を吸収する.
  const gammaLookup = PRESSURE_GAMMA as Record<StrokePen, { width: number; alpha: number }>;
  const gammaTable = (penKind !== undefined ? gammaLookup[penKind] : undefined) ?? {
    width: 1,
    alpha: 1,
  };

  // 2 軸独立 (consult 2026-05-09 / Step 5c): 各軸の Enabled / min / gamma で
  // 独立に mapping. **片軸 OFF のときは `1.0` を明示返却** する (consult
  // 2026-05-09 ユーザ実機 fb の fix): 旧仕様 (= `undefined` で omit) では
  // drawing-core 側 fallback `pt.pAlpha ?? pt.p ?? 1.0` (旧 client 後方互換用)
  // が効いてしまい、「ブラシ濃さ筆圧 OFF + 太さ ON」のときに `pt.p` (= 太さ用
  // の値) が alpha 軸にも漏れて「濃さ筆圧 OFF にしても alpha が動的に変わる」
  // バグになっていた. `1.0` を明示送信することで drawing-core 側で当該軸固定
  // (= scale=1.0、= ペン UI 設定そのまま) の意図が伝わる.
  const p = settings.widthEnabled
    ? applyPressureCurve(settings.widthMin, gammaTable.width, pRaw)
    : 1.0;
  const pAlpha = settings.alphaEnabled
    ? applyPressureCurve(settings.alphaMin, gammaTable.alpha, pRaw)
    : 1.0;

  return { p, pAlpha };
};

/**
 * 統一 mapping 内部ヘルパ.
 *   `min + (1 - min) * pow(p, gamma)`
 * - `min` を 0..1 に clamp、`p` を 0..1 に clamp
 * - `gamma` は正の有限数 (= drawing-core `PRESSURE_GAMMA` から来る前提なので
 *   defensive 検査は最小限). 0 や負・NaN なら 1 にフォールバック.
 *
 * `applyPressureMin` (= 線形版) との関係: `gamma=1` なら数学的に同値. 公開
 * API として `applyPressureMin` は残し (既存テスト + シンプル経路用)、累乗
 * 必要時はこのヘルパを使う.
 */
const applyPressureCurve = (min: number, gamma: number, pRaw: number): number => {
  const p = Math.max(0, Math.min(1, pRaw));
  const m = Math.max(0, Math.min(1, min));
  const g = Number.isFinite(gamma) && gamma > 0 ? gamma : 1;
  return m + (1 - m) * p ** g;
};

// ═══════════════════════════════════════════════════════════════════════════
// localStorage adapter (bellOnRoomin と同流儀)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 永続化用 minimal storage interface.
 * `globalThis.localStorage` で satisfies; tests pass an in-memory mock.
 */
export interface PenPressureStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** localStorage を試しに probe して返す. 失敗したら null. bellOnRoomin と同流儀. */
const probeBrowserStorage = (): PenPressureStorage | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = `${STORAGE_KEY_PREFIX}:__probe__`;
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

/**
 * storage に保存された JSON を validate して PenPressureSettings に変換.
 * 不正値はデフォルトに倒す (= 部分的に正しい場合は正しい部分だけ採用).
 */
const parseStored = (raw: string, fallback: PenPressureSettings): PenPressureSettings => {
  try {
    const v: unknown = JSON.parse(raw);
    if (typeof v !== 'object' || v === null) return fallback;
    const o = v as Record<string, unknown>;
    const widthEnabled =
      typeof o.widthEnabled === 'boolean' ? o.widthEnabled : fallback.widthEnabled;
    const alphaEnabled =
      typeof o.alphaEnabled === 'boolean' ? o.alphaEnabled : fallback.alphaEnabled;
    const widthMin =
      typeof o.widthMin === 'number' &&
      Number.isFinite(o.widthMin) &&
      o.widthMin >= 0 &&
      o.widthMin <= 1
        ? o.widthMin
        : fallback.widthMin;
    const alphaMin =
      typeof o.alphaMin === 'number' &&
      Number.isFinite(o.alphaMin) &&
      o.alphaMin >= 0 &&
      o.alphaMin <= 1
        ? o.alphaMin
        : fallback.alphaMin;
    return { widthEnabled, widthMin, alphaEnabled, alphaMin };
  } catch {
    return fallback;
  }
};

/** localStorage アダプタ. storage が使えないときも silent に no-op で動く. */
export interface PenPressureStore {
  /** 現在の設定を読む. 不在 / 不正値 / storage 不可ならデフォルト値. */
  read(penKind: StrokePen): PenPressureSettings;
  /** 設定を書き込む. storage 不可なら silent no-op. */
  write(penKind: StrokePen, settings: PenPressureSettings): void;
}

export interface CreatePenPressureStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * return a no-op store if unavailable.
   *
   * Pass `null` to **force** the no-storage path (= read always returns
   * default, write is no-op). Tests use this to verify graceful degradation.
   */
  storage?: PenPressureStorage | null;
}

const keyFor = (penKind: StrokePen): string => `${STORAGE_KEY_PREFIX}:${penKind}`;

export const createPenPressureStore = (
  opts: CreatePenPressureStoreOptions = {},
): PenPressureStore => {
  const explicitStorage = opts.storage;
  const storage = explicitStorage === null ? null : (explicitStorage ?? probeBrowserStorage());

  return {
    read(penKind) {
      const fallback = DEFAULT_PEN_PRESSURE[penKind];
      if (storage === null) return fallback;
      try {
        const v = storage.getItem(keyFor(penKind));
        if (v === null) return fallback;
        return parseStored(v, fallback);
      } catch {
        return fallback;
      }
    },
    write(penKind, settings) {
      if (storage === null) return;
      try {
        storage.setItem(keyFor(penKind), JSON.stringify(settings));
      } catch {
        // ignore quota / cookie-blocked
      }
    },
  };
};
